import { createSelector } from 'reselect';
import {
  compose,
  head,
  either,
  prop,
  propOr,
  values,
  sort,
  descend,
  filter,
  complement,
  all,
  keys,
  map,
  includes,
  pipe,
  reject,
  isNil,
  isEmpty,
  __,
  path,
  mergeAll,
  flatten,
  propEq,
} from 'ramda';
import { generateSlugsRegistry } from './generateSlugs';
import { isNilOrEmpty } from '../../shared/utils/functools';

export const selectContentRegistryDomain = state => state.content;

export const selectEntities = createSelector(
  selectContentRegistryDomain,
  prop('entities')
);

export const selectWebsiteConfig = createSelector(
  selectEntities,
  compose(
    head,
    values,
    prop('websiteConfig')
  )
);

export const selectMagazine = createSelector(
  selectWebsiteConfig,
  propOr('', 'magazine')
);

export const selectAppConfig = createSelector(
  selectEntities,
  compose(
    head,
    values,
    prop('appConfig')
  )
);

export const selectFooter = createSelector(
  selectEntities,
  compose(
    head,
    values,
    prop('footer')
  )
);

export const selectIsRehydrated = createSelector(
  selectContentRegistryDomain,
  prop('isRehydrated')
);

export const selectIsFooterInitialized = createSelector(
  selectFooter,
  complement(isNilOrEmpty)
);

export const selectIsInitialised = createSelector(
  selectAppConfig,
  selectWebsiteConfig,
  selectFooter,
  (appConfig, websiteConfig, footerConfig) => all(complement(isNilOrEmpty), [appConfig, websiteConfig, footerConfig])
);

export const selectCancellationPolicy = createSelector(
  selectAppConfig,
  prop('cancellationPolicy')
);

export const selectAssets = createSelector(
  selectEntities,
  prop('assets')
);

export const selectCategories = createSelector(
  selectEntities,
  prop('categories')
);

export const selectQuotes = createSelector(
  selectEntities,
  prop('quotes')
);

export const selectGuides = createSelector(
  selectEntities,
  prop('guides')
);

export const selectStories = createSelector(
  selectEntities,
  prop('stories')
);

export const selectNewsletters = createSelector(
  selectEntities,
  prop('newsletters')
);

export const selectAllExperiences = createSelector(
  selectEntities,
  prop('experiences')
);

export const selectExperiences = createSelector(
  selectEntities,
  compose(
    filter(propEq('activeInWeb', true)),
    prop('experiences')
  )
);

export const selectExperienceById = id =>
  createSelector(
    selectAllExperiences,
    prop(id)
  );

export const selectSortedExperiencesArray = createSelector(
  selectExperiences,
  compose(
    sort(descend(propOr(0, 'orderPriority'))),
    values
  )
);

export const selectMarkets = createSelector(
  selectEntities,
  prop('markets')
);

export const selectRegions = createSelector(
  selectEntities,
  prop('regions')
);

export const selectExperiencesRegions = createSelector(
  selectRegions,
  pipe(
    values,
    map(({ id, name, experiences }) =>
      map(experience => ({ [path(['sys', 'id'])(experience)]: { id, name } }))(experiences)
    ),
    flatten,
    mergeAll
  )
);

export const selectMarketsIds = createSelector(
  selectMarkets,
  keys
);

export const selectFeaturedExperiencesSectionsWithDrafts = createSelector(
  selectEntities,
  prop('featuredExperiencesSections')
);

export const selectFeaturedExperiencesSections = createSelector(
  selectExperiences,
  selectFeaturedExperiencesSectionsWithDrafts,
  (experiences, featuredExperiencesSections) =>
    pipe(
      reject(
        compose(
          either(isNil, isEmpty),
          prop('experiences')
        )
      ),
      map(featuredExperienceSection => ({
        ...featuredExperienceSection,
        experiences: filter(includes(__, keys(experiences)), featuredExperienceSection.experiences),
      }))
    )(featuredExperiencesSections)
);

export const selectPartnerEntities = createSelector(
  selectEntities,
  prop('partners')
);

export const selectVendors = createSelector(
  selectEntities,
  prop('vendors')
);

export const selectKioskModes = createSelector(
  selectEntities,
  prop('kioskModes')
);

export const selectSlugs = createSelector(
  selectEntities,
  generateSlugsRegistry
);

export const selectExperiencesSlugs = createSelector(
  selectSlugs,
  prop('experiences')
);

export const selectKioskSlugs = createSelector(
  selectSlugs,
  prop('kioskModes')
);

export const selectMarketSlugs = createSelector(
  selectSlugs,
  prop('markets')
);

export const selectIsFetching = createSelector(
  selectContentRegistryDomain,
  prop('isFetching')
);

export const selectIsFetchingAllMarkets = createSelector(
  selectIsFetching,
  prop('allMarkets')
);

export const selectIsFetchingMarketExperiences = createSelector(
  selectIsFetching,
  propOr({}, 'marketExperiences')
);
